import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { useLazyQuery } from '@apollo/client';
import ModalUtils from 'utils/ModalUtils';
import { FetchPolicy } from 'utils/enum/Core';
import Select from 'components/widgets/Select';
import EditorStyles from 'styles/widgets/EditorControlStyles';

const useStyles = makeStyles(() => EditorStyles.selectStyles());

const buildOptions = ({
    sourceData,
    idField,
    descriptionField,
    allowEmptyLine,
    defaultEmptyLineId,
    defaultEmptyLineText,
}) => {
    const dataList = (sourceData || []).map((item) => ({
        ...item,
        value: item[idField] ?? item.value,
        label: item[descriptionField] ?? item.label,
    }));

    if (allowEmptyLine) {
        dataList.unshift({ value: defaultEmptyLineId, label: defaultEmptyLineText });
    }

    return dataList;
};

const BaseDropdownQuery = (props) => {
    const [records, setRecords] = useState([]);
    const classes = useStyles();

    const {
        name,
        value,
        onChange,
        dataSource,
        editorCellObject,
        className,
        allowEmptyLine,
        defaultEmptyLineId,
        defaultEmptyLineText,
        customDataHandler,
        ...other
    } = props;

    const {
        rootData,
        idField,
        descriptionField,
        localData,
        additionalFieldsReturned,
    } = dataSource;

    const getRootData = (data) => {
        if (!rootData) return data || [];

        return rootData.split('.').reduce((acc, key) => acc?.[key], data) || [];
    };

    const handleChange = useCallback((id, newValue) => {
        const additionalFieldsResult = {};

        if (additionalFieldsReturned) {
            const recordSelected = records.find(
                (item) => String(item[idField]) === String(newValue),
            );

            if (recordSelected) {
                additionalFieldsReturned.forEach((item) => {
                    additionalFieldsResult[item] = recordSelected[item];
                });
            }
        }

        onChange(id, newValue, editorCellObject, additionalFieldsResult);
    }, [additionalFieldsReturned, records, idField, onChange, editorCellObject]);

    const handleCreate = useCallback(
        (inputValue) => {
            const newValue = { value: inputValue, label: inputValue };
            setRecords((prev) => [...prev, newValue]);
            handleChange(name, inputValue);
        },
        [handleChange, name],
    );

    useEffect(() => {
        if (localData) {
            const currentData = buildOptions({
                sourceData: localData,
                idField,
                descriptionField,
                allowEmptyLine,
                defaultEmptyLineId,
                defaultEmptyLineText,
            });

            if (other.creatable) {
                const isValueAnOption = currentData.find((item) => item.value === value);
                if (!isValueAnOption && value) {
                    currentData.push({ label: value, value });
                }
            }

            setRecords(currentData);
        }
    }, [
        localData,
        idField,
        descriptionField,
        allowEmptyLine,
        defaultEmptyLineId,
        defaultEmptyLineText,
        other.creatable,
        value,
    ]);

    return {
        records,
        setRecords,
        classes,
        getRootData,
        handleChange,
        handleCreate,
        propsForSelect: {
            name,
            value,
            className,
            other,
        },
        config: {
            idField,
            descriptionField,
            allowEmptyLine,
            defaultEmptyLineId,
            defaultEmptyLineText,
            customDataHandler,
        },
    };
};

const DropdownWithRemoteData = (props) => {
    const state = BaseDropdownQuery(props);
    const { dataSource } = props;
    const { query, variables, fetchPolicy } = dataSource;

    const {
        setRecords,
        getRootData,
        handleChange,
        handleCreate,
        records,
        classes,
        propsForSelect,
        config,
    } = state;

    const [rawLoadData] = useLazyQuery(query, {
        onCompleted: (data) => {
            const recordList = config.customDataHandler
                ? config.customDataHandler(data, dataSource.rootData)
                : getRootData(data);

            const dataList = buildOptions({
                sourceData: recordList,
                idField: config.idField,
                descriptionField: config.descriptionField,
                allowEmptyLine: config.allowEmptyLine,
                defaultEmptyLineId: config.defaultEmptyLineId,
                defaultEmptyLineText: config.defaultEmptyLineText,
            });

            setRecords(dataList);
        },
        onError: (queryError) => {
            ModalUtils.errorMessage(null, queryError);
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: fetchPolicy ?? FetchPolicy.NETWORK_ONLY,
    });

    useEffect(() => {
        rawLoadData({
            variables: variables || null,
        });
    }, [rawLoadData, variables]);

    return (
        <Select
            name={propsForSelect.name}
            options={records}
            value={propsForSelect.value}
            onChange={handleChange}
            onCreate={(createdValue) => (propsForSelect.other.creatable ? handleCreate(createdValue) : null)}
            className={clsx(
                'basic-multi-select select-bootstrap form-control-sm',
                classes.selectSM,
                propsForSelect.className,
            )}
            {...propsForSelect.other}
        />
    );
};

const DropdownWithLocalData = (props) => {
    const {
        records,
        handleChange,
        handleCreate,
        classes,
        propsForSelect,
    } = BaseDropdownQuery(props);

    return (
        <Select
            name={propsForSelect.name}
            options={records}
            value={propsForSelect.value}
            onChange={handleChange}
            onCreate={(createdValue) => (propsForSelect.other.creatable ? handleCreate(createdValue) : null)}
            className={clsx(
                'basic-multi-select select-bootstrap form-control-sm',
                classes.selectSM,
                propsForSelect.className,
            )}
            {...propsForSelect.other}
        />
    );
};

const DropdownQuery = (props) => {
    const { dataSource } = props;
    const { query } = dataSource;

    if (!query) {
        return <DropdownWithLocalData {...props} />;
    }
    return <DropdownWithRemoteData {...props} />;
};

const dataSourcePropType = PropTypes.shape({
    query: PropTypes.any,
    rootData: PropTypes.string,
    idField: PropTypes.string,
    descriptionField: PropTypes.string,
    variables: PropTypes.object,
    localData: PropTypes.array,
    additionalFieldsReturned: PropTypes.array,
    fetchPolicy: PropTypes.string,
});

DropdownWithRemoteData.propTypes = {
    dataSource: dataSourcePropType.isRequired,
};

DropdownWithLocalData.propTypes = {
    dataSource: dataSourcePropType.isRequired,
};
DropdownQuery.propTypes = {
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
    dataSource: dataSourcePropType.isRequired,
    editorCellObject: PropTypes.object,
    className: PropTypes.string,
    defaultEmptyLineId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    defaultEmptyLineText: PropTypes.string,
    allowEmptyLine: PropTypes.bool,
    customDataHandler: PropTypes.func,
};

DropdownQuery.defaultProps = {
    value: null,
    className: '',
    editorCellObject: {},
    defaultEmptyLineId: 0,
    defaultEmptyLineText: 'Select ...',
    allowEmptyLine: true,
    customDataHandler: null,
};

export default DropdownQuery;
